<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsSegmented from '@/components/pds/PdsSegmented.vue';
import { computed, ref } from 'vue';
import PageBar from '../components/PageBar.vue';
import PageTitle from '../components/PageTitle.vue';
import { MOCK_PURCHASE_DAYS } from '../fake/purchase.mock';
import { formatDate, formatTime } from '../format';
import { usePdsOrdersStore } from '../stores/orders.store';

const store = usePdsOrdersStore();
const horizon = ref<'3' | '7' | '14'>('3');

const options = [
  { value: '3' as const, label: '3 jours' },
  { value: '7' as const, label: '7 jours' },
  { value: '14' as const, label: '14 jours' },
];

const days = computed(() =>
  MOCK_PURCHASE_DAYS.filter((day) => day.daysAhead <= Number(horizon.value)),
);
const lines = computed(() => days.value.flatMap((day) => day.lines));
const pieces = computed(() =>
  lines.value.reduce((sum, line) => sum + line.quantity, 0),
);
const orderCount = computed(
  () => new Set(lines.value.flatMap((l) => l.orders.map((o) => o.number))).size,
);
</script>

<template>
  <PageBar tall>
    <PageTitle
      title="À commander"
      subtitle="Les Click & Collect enregistrés, cumulés par produit · par jour d’échéance"
    />
    <template #aside>
      <p class="text-encre-douce hidden text-[12px] leading-[17px] lg:block">
        Arrêté au {{ formatDate(store.now).toLowerCase() }} ·
        {{ formatTime(store.now) }}
      </p>
    </template>
  </PageBar>

  <main class="no-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-5">
    <PdsSegmented v-model="horizon" :options="options" class="w-fit" />

    <p class="flex flex-wrap items-baseline gap-x-3 px-[2px] pt-[14px] pb-4">
      <span
        class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
      >
        À commander · {{ horizon }} prochains jours
      </span>
      <span class="text-encre-douce text-[12px] sm:ml-auto">
        {{ lines.length }} références · {{ pieces }} pièces ·
        {{ orderCount }} commandes
      </span>
    </p>

    <div class="gap-4 lg:columns-2">
      <section
        v-for="day in days"
        :key="day.label"
        class="bg-carte mb-4 break-inside-avoid overflow-hidden rounded-[24px]"
      >
        <header
          class="border-filet-doux flex flex-wrap items-baseline gap-x-3 border-b px-4 py-3"
        >
          <h2
            class="text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
          >
            {{ day.label }}
          </h2>
          <p class="text-encre-douce text-[12px] leading-[17px]">
            {{ day.lines.length }} références ·
            {{ day.lines.reduce((s, l) => s + l.quantity, 0) }} pièces · à
            livrer {{ day.deliverOn }}
          </p>
        </header>
        <div
          v-for="line in day.lines"
          :key="line.name"
          class="border-filet-doux flex items-baseline gap-3 border-t px-4 py-3 first-of-type:border-t-0"
        >
          <span class="text-[24px] leading-[28px] font-bold">
            {{ line.quantity }}
          </span>
          <span class="min-w-0 flex-1">
            <span class="block text-[18px] leading-[22px] font-semibold">
              {{ line.name }}
            </span>
            <span
              v-if="line.note"
              class="text-encre-douce block text-[12px] leading-[17px]"
            >
              {{ line.note }}
            </span>
          </span>
          <span class="text-encre-douce text-right text-[14px] leading-[20px]">
            {{
              line.orders.map((o) => `#${o.number} (${o.quantity})`).join(' · ')
            }}
          </span>
        </div>
      </section>
    </div>
  </main>

  <footer
    class="bg-carte border-filet flex min-h-[94px] shrink-0 items-center justify-end border-t px-4 py-4 sm:px-5"
  >
    <PdsButton fit disabled title="Bientôt disponible">
      Imprimer le bon de commande
    </PdsButton>
  </footer>
</template>
